import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import clientReviewService from '../../../services/clientReviewService';

const ReviewSubmissionForm = ({ onReviewSubmitted }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(true);

  const [formData, setFormData] = useState({
    reviewerName: '',
    reviewerEmail: user?.email || '',
    reviewerPosition: '',
    starRating: 0,
    reviewText: '',
    testimonialType: 'written',
    costSavingsKina: '',
    timeEfficiencyGainPercentage: '',
    regulatoryAdherenceScore: '',
    departmentType: '',
    organizationSize: '',
    useCase: ''
  });

  useEffect(() => {
    checkEligibility();
  }, []);

  const checkEligibility = async () => {
    try {
      setCheckingEligibility(true);
      // For demo, assume first company
      const { data: companies } = await supabase?.from('company_settings')?.select('id')?.limit(1);
      if (companies?.[0]) {
        const result = await clientReviewService?.checkEligibility(companies?.[0]?.id);
        setEligibility(result);
      }
    } catch (err) {
      console.error('Eligibility check failed:', err);
    } finally {
      setCheckingEligibility(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleStarClick = (rating) => {
    setFormData(prev => ({ ...prev, starRating: rating }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (formData?.starRating === 0) {
      setError('Please select a star rating');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Get company ID
      const { data: companies } = await supabase?.from('company_settings')?.select('id')?.limit(1);
      if (!companies?.[0]) {
        throw new Error('Company not found');
      }

      const reviewData = {
        companyId: companies?.[0]?.id,
        reviewerName: formData?.reviewerName,
        reviewerEmail: formData?.reviewerEmail,
        reviewerPosition: formData?.reviewerPosition,
        starRating: formData?.starRating,
        reviewText: formData?.reviewText,
        testimonialType: formData?.testimonialType,
        costSavingsKina: parseFloat(formData?.costSavingsKina) || 0,
        timeEfficiencyGainPercentage: parseFloat(formData?.timeEfficiencyGainPercentage) || 0,
        regulatoryAdherenceScore: parseFloat(formData?.regulatoryAdherenceScore) || 0,
        departmentType: formData?.departmentType,
        organizationSize: formData?.organizationSize,
        useCase: formData?.useCase,
        usagePeriodStart: eligibility?.usagePeriodStart,
        usagePeriodEnd: new Date()?.toISOString()?.split('T')?.[0],
        usageDays: eligibility?.usageDays
      };

      const newReview = await clientReviewService?.createReview(reviewData);
      await clientReviewService?.submitReview(newReview?.id);

      setSuccess(true);
      setFormData({
        reviewerName: '',
        reviewerEmail: user?.email || '',
        reviewerPosition: '',
        starRating: 0,
        reviewText: '',
        testimonialType: 'written',
        costSavingsKina: '',
        timeEfficiencyGainPercentage: '',
        regulatoryAdherenceScore: '',
        departmentType: '',
        organizationSize: '',
        useCase: ''
      });

      if (onReviewSubmitted) {
        onReviewSubmitted();
      }

      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err?.message || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  if (checkingEligibility) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-12 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">Checking eligibility...</p>
      </div>
    );
  }

  if (!eligibility?.eligible) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-12 text-center">
        <Icon name="Lock" size={48} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Review Submission Not Available</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">{eligibility?.reason}</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
      <div className="p-6 border-b border-gray-200 dark:border-slate-700">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Submit Your Review</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Share your experience with PNG HR Analytics and help other organizations
        </p>
      </div>
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Success Message */}
        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Icon name="CheckCircle" size={20} className="text-green-600 dark:text-green-400" />
              <p className="text-sm text-green-600 dark:text-green-400">Review submitted successfully! It will be reviewed before publication.</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400" />
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* Reviewer Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Your Name"
            value={formData?.reviewerName}
            onChange={(e) => handleInputChange('reviewerName', e?.target?.value)}
            required
          />
          <Input
            label="Email"
            type="email"
            value={formData?.reviewerEmail}
            onChange={(e) => handleInputChange('reviewerEmail', e?.target?.value)}
            required
          />
          <Input
            label="Position/Title"
            value={formData?.reviewerPosition}
            onChange={(e) => handleInputChange('reviewerPosition', e?.target?.value)}
            required
          />
        </div>

        {/* Star Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Overall Rating <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5]?.map((rating) => (
              <button
                key={rating}
                type="button"
                onClick={() => handleStarClick(rating)}
                className="transition-transform hover:scale-110"
              >
                <Icon
                  name="Star"
                  size={32}
                  className={rating <= formData?.starRating ? 'text-amber-400 fill-amber-400' : 'text-gray-300 dark:text-gray-600'}
                />
              </button>
            ))}
            <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
              {formData?.starRating > 0 ? `${formData?.starRating} out of 5` : 'Select rating'}
            </span>
          </div>
        </div>

        {/* Review Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Your Review <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData?.reviewText}
            onChange={(e) => handleInputChange('reviewText', e?.target?.value)}
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
            placeholder="Share your experience with PNG HR Analytics..."
            required
          />
        </div>

        {/* Compliance Success Metrics */}
        <div className="border-t border-gray-200 dark:border-slate-700 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Compliance Success Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Cost Savings (PNG Kina)"
              type="number"
              value={formData?.costSavingsKina}
              onChange={(e) => handleInputChange('costSavingsKina', e?.target?.value)}
              placeholder="e.g., 380000"
            />
            <Input
              label="Time Efficiency Gain (%)"
              type="number"
              value={formData?.timeEfficiencyGainPercentage}
              onChange={(e) => handleInputChange('timeEfficiencyGainPercentage', e?.target?.value)}
              placeholder="e.g., 65"
            />
            <Input
              label="Regulatory Adherence Score (%)"
              type="number"
              value={formData?.regulatoryAdherenceScore}
              onChange={(e) => handleInputChange('regulatoryAdherenceScore', e?.target?.value)}
              placeholder="e.g., 98"
            />
          </div>
        </div>

        {/* Organization Details */}
        <div className="border-t border-gray-200 dark:border-slate-700 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Organization Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Department Type</label>
              <select
                value={formData?.departmentType}
                onChange={(e) => handleInputChange('departmentType', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
              >
                <option value="">Select type</option>
                <option value="government">Government</option>
                <option value="provincial">Provincial</option>
                <option value="private">Private Sector</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Organization Size</label>
              <select
                value={formData?.organizationSize}
                onChange={(e) => handleInputChange('organizationSize', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
              >
                <option value="">Select size</option>
                <option value="small">Small (1-50)</option>
                <option value="medium">Medium (51-200)</option>
                <option value="large">Large (201+)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Primary Use Case</label>
              <select
                value={formData?.useCase}
                onChange={(e) => handleInputChange('useCase', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
              >
                <option value="">Select use case</option>
                <option value="compliance">Compliance Management</option>
                <option value="payroll">Payroll Processing</option>
                <option value="workforce">Workforce Analytics</option>
                <option value="document">Document Management</option>
              </select>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-slate-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Usage Period: {eligibility?.usageDays || 0} days
          </p>
          <Button type="submit" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit Review'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ReviewSubmissionForm;